import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Boxes, ArrowRight, Loader2, Check } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { btnPrimary } from "./primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENT_COPY = {
  create_bulkcontainer: { title: "Create Your BulkContainer™", desc: "Start a factory-direct bulk order and manage it inside one Workspace." },
  create_workspace: { title: "Create Your Free Workspace", desc: "Set up your DeMaxtore Workspace and start sourcing in bulk." },
  sign_in: { title: "Sign in to your Workspace", desc: "Enter your work email to receive a secure sign-in link." },
};

export default function LeadModal({ open, onOpenChange, intent }) {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const copy = INTENT_COPY[intent] || INTENT_COPY.create_workspace;

  const reset = () => { setEmail(""); setCompany(""); setDone(false); setLoading(false); };

  const handleOpenChange = (v) => {
    if (!v) setTimeout(reset, 200);
    onOpenChange(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Please enter your work email."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        email: email.trim(),
        company: company.trim() || null,
        intent,
      });
      setDone(true);
      toast.success("You're on the list!", { description: "Our procurement team will reach out shortly." });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? "Please enter a valid email." : "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="lead-modal"
        className="max-w-md rounded-2xl border-slate-200 bg-white p-0 shadow-[0_30px_80px_-20px_rgba(10,17,40,0.35)]"
      >
        <div className="p-7">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0052FF] text-white shadow-[0_10px_24px_-8px_rgba(0,82,255,0.6)]">
            <Boxes className="h-5 w-5" />
          </span>

          {done ? (
            <div className="mt-5" data-testid="lead-success">
              <DialogHeader>
                <DialogTitle className="font-display text-xl font-bold text-[#0A1128]">
                  Thank you &mdash; you&apos;re in.
                </DialogTitle>
                <DialogDescription className="mt-2 text-[14px] leading-relaxed text-[#64748B]">
                  We&apos;ve received your request. A DeMaxtore procurement specialist will be in touch
                  to set up your Workspace.
                </DialogDescription>
              </DialogHeader>
              <div className="mt-6 flex items-center gap-2 rounded-xl bg-[#ECFDF5] px-4 py-3 text-[13px] font-medium text-[#059669]">
                <Check className="h-4 w-4" strokeWidth={3} /> Request confirmed
              </div>
            </div>
          ) : (
            <>
              <DialogHeader className="mt-5">
                <DialogTitle className="font-display text-xl font-bold text-[#0A1128]">{copy.title}</DialogTitle>
                <DialogDescription className="mt-2 text-[14px] leading-relaxed text-[#64748B]">{copy.desc}</DialogDescription>
              </DialogHeader>

              <form onSubmit={submit} className="mt-6 space-y-3">
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-[#0A1128]">Work email</label>
                  <input
                    data-testid="lead-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[14px] text-[#0A1128] outline-none transition-colors placeholder:text-slate-400 focus:border-[#0052FF] focus:ring-2 focus:ring-[#0052FF]/15"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-[#0A1128]">
                    Company <span className="text-[#94A3B8]">(optional)</span>
                  </label>
                  <input
                    data-testid="lead-company-input"
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Company name"
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[14px] text-[#0A1128] outline-none transition-colors placeholder:text-slate-400 focus:border-[#0052FF] focus:ring-2 focus:ring-[#0052FF]/15"
                  />
                </div>
                <button
                  data-testid="lead-submit-button"
                  type="submit"
                  disabled={loading}
                  className={btnPrimary + " w-full disabled:opacity-70"}
                >
                  {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>) : (<>Continue <ArrowRight className="h-4 w-4" /></>)}
                </button>
                <p className="text-center text-[12px] text-[#94A3B8]">
                  Factory Direct Purchasing · No Product Markups
                </p>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
